/**
 * Prompt library. Prompts are stored rows, not hard-coded strings, so an admin can
 * add, edit and retire them without a deploy.
 *
 * Two things about a prompt decide where it turns up:
 *
 *   `target_field`  its classification — what the instruction is written to work on.
 *                   A prompt is offered first in the dialog of its own field and
 *                   stays selectable in the others, so an instruction written once
 *                   can be used anywhere it makes sense. The classifications differ
 *                   in kind, not just in name: a text prompt is handed the words in
 *                   a field, a transcription prompt is handed a picture of a page,
 *                   and the page groups them that way (see lib/promptFields.js).
 *   `sort_order`    its priority *within* that classification, set with the arrows
 *                   below. Whatever sits at the top here is what an editor is
 *                   offered first for that field — on the update form, in the file
 *                   importer, in the studio — and what the one-click buttons fall
 *                   back to when no default prompt is mapped to the field.
 *
 * `writes_to` matters for extraction prompts, which read one field and write another.
 */
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../App'
import { api, useApi, qs } from '../lib/api'
import { PROMPT_FIELDS, promptFieldGroups, promptFieldLabel } from '../lib/promptFields'
import { ModelSelect, EnumSelect } from '../components/Select'
import {
  Card, Field, Badge, Modal, Switch, Spinner, Empty, Notice, Tabs,
  QueryState, ConfirmModal, useToast, fmtInt, fmtDateTime,
} from '../components/ui'
import { useRelabel, relabelText, usePartnerTerm } from '../lib/terms'

const TARGET_FIELDS = PROMPT_FIELDS.map((f) => ({ value: f.value, label: f.label }))

const MODES = [
  { value: 'proofread', label: 'Proofread' },
  { value: 'rewrite', label: 'Rewrite' },
  { value: 'summarize', label: 'Summarize' },
  { value: 'extract', label: 'Extract' },
  { value: 'translate', label: 'Translate' },
  { value: 'custom', label: 'Custom' },
]

const blank = {
  name: '',
  description: '',
  instruction: '',
  target_field: 'newsletters.body_html',
  mode: 'rewrite',
  writes_to: '',
  model_id: null,
  temperature: 0.3,
  is_active: true,
}

/** Priority first, then name — the same order the server and the pickers use. */
const byPriority = (a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.name).localeCompare(String(b.name))

function PromptEditor({ open, onClose, initial, onSaved }) {
  const toast = useToast()
  const [form, setForm] = useState(initial ?? blank)
  const [busy, setBusy] = useState(false)
  const isEdit = !!initial?.id
  const set = (patch) => setForm((f) => ({ ...f, ...patch }))
  const field = PROMPT_FIELDS.find((f) => f.value === form.target_field)

  // The same lookup the AI buttons use to label themselves — what model (and
  // prompt) a field falls back to with nothing named. Keyed on the target field
  // rather than this prompt's own id, since that is what decides the fallback.
  const resolved = useApi(
    ['ai-resolve', form.target_field],
    `ai/resolve${qs({ field_key: form.target_field })}`,
    { staleTime: 60_000 }
  )
  const mappedModel = resolved.data?.ok ? resolved.data.model : null

  const submit = async () => {
    if (!form.name.trim() || !form.instruction.trim()) {
      toast.error('A name and an instruction are both required')
      return
    }
    setBusy(true)
    try {
      if (isEdit) {
        await api(`prompts/${initial.id}`, { method: 'PUT', body: form })
        toast.ok('Prompt saved')
      } else {
        await api('prompts', { method: 'POST', body: form })
        toast.ok('Prompt created — it starts at the bottom of its list')
      }
      onSaved()
      onClose()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={isEdit ? `Edit prompt — ${initial.name}` : 'New prompt'}
      footer={
        <>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? <Spinner /> : isEdit ? 'Save prompt' : 'Create prompt'}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {initial?.is_builtin ? (
            <>
              <span className="spacer" />
              <Badge>built-in</Badge>
            </>
          ) : null}
        </>
      }
    >
      <div className="col">
        <div className="field-row">
          <Field label="Name" required>
            <input className="input" value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field
            label="Applies to"
            required
            hint={field?.hint ?? 'Where this prompt is offered first — every prompt stays selectable everywhere'}
          >
            <EnumSelect
              value={form.target_field}
              onChange={(v) => set({ target_field: v || 'newsletters.body_html' })}
              options={TARGET_FIELDS}
              isClearable={false}
            />
          </Field>
          <Field label="Mode">
            <EnumSelect value={form.mode} onChange={(v) => set({ mode: v || 'rewrite' })} options={MODES} isClearable={false} />
          </Field>
        </div>

        {isEdit && initial.target_field !== form.target_field && (
          <Notice tone="info" icon="info">
            Moving a prompt to another classification puts it at the bottom of that
            list. Use the arrows on the card to give it a priority there.
          </Notice>
        )}

        <Field label="Short description" hint="Shown next to the prompt in the picker">
          <input className="input" value={form.description ?? ''} onChange={(e) => set({ description: e.target.value })} />
        </Field>

        <Field
          label="Instruction"
          required
          hint="Written to the model verbatim. Be explicit about what must not change — names, initials and facts."
        >
          <textarea
            className="textarea textarea-lg"
            value={form.instruction}
            onChange={(e) => set({ instruction: e.target.value })}
            placeholder="Correct spelling and grammar without changing meaning. Return only the corrected text."
          />
        </Field>

        <div className="field-row">
          {form.mode === 'extract' && (
            <Field label="Writes result to" hint="Where the extracted text should land">
              <EnumSelect
                value={form.writes_to || null}
                onChange={(v) => set({ writes_to: v ?? '' })}
                options={TARGET_FIELDS}
              />
            </Field>
          )}
          {/* Every model, whatever this prompt applies to. The list used to follow
              the target field, which meant a prompt for reading text out of a
              picture could only be given a Cloudflare vision model — the one thing
              it was least likely to want. Naming a model here is a deliberate act;
              the run reports what the model made of it. */}
          <Field
            label="Model / Model override"
            hint={
              mappedModel
                ? `Runs on ${mappedModel.label}${mappedModel.enabled ? '' : ' (disabled)'} — the model mapped to “${field?.label ?? form.target_field}” on AI Settings → AI Models. Choose one below to override it for this prompt only.`
                : `No model is mapped to “${field?.label ?? form.target_field}” yet (AI Settings → AI Models). Choose one below to give this prompt its own.`
            }
          >
            <ModelSelect
              value={form.model_id}
              onChange={(v) => set({ model_id: v })}
              placeholder={mappedModel ? `${mappedModel.label} (field default)` : 'Use the mapped default'}
            />
          </Field>
          <Field label="Temperature" hint="Lower is more literal. 0.2–0.4 suits proofreading.">
            <input
              className="input"
              type="number"
              step="0.1"
              min="0"
              max="1.5"
              value={form.temperature}
              onChange={(e) => set({ temperature: Number(e.target.value) })}
            />
          </Field>
        </div>

        <Switch checked={form.is_active} onChange={(v) => set({ is_active: v })} label="Active" />
      </div>
    </Modal>
  )
}

/**
 * One classification: its prompts in priority order, top first.
 *
 * The arrows move a prompt within this list and nowhere else, which is the whole
 * point of the priority being per classification — promoting a transcription prompt
 * has no business changing what the update form offers first.
 *
 * Inactive prompts stay in the list and keep their place. They are invisible to the
 * pickers, so their rank does nothing until they are switched back on, and dropping
 * them out of the order would silently renumber everything below them.
 */
function FieldSection({ field, rows, user, busy, onMove, onEdit, onToggle, onDelete }) {
  const t = useRelabel()
  const label = relabelText(field.label, usePartnerTerm())
  const ids = rows.map((r) => r.id)

  return (
    <div className="col" style={{ marginTop: 14 }}>
      <div className="row">
        {/* The field's name and note are this application's words, so they follow the
            installation's term. The prompts under them are records somebody wrote and
            can edit, and they are shown as written. */}
        <h3>{t(field.label)}</h3>
        <Badge>{fmtInt(rows.length)}</Badge>
        <span className="spacer" />
        <span className="small subtle">{t(field.hint)}</span>
      </div>

      {!rows.length ? (
        <Empty icon="edit" title="No prompts for this field yet">
          <p className="small muted">{t(field.hint)}</p>
        </Empty>
      ) : (
        rows.map((p, i) => (
          <div className="card" key={p.id}>
            <div className="card-body">
              <div className="row" style={{ marginBottom: 6 }}>
                <Badge
                  tone={i === 0 ? 'ok' : undefined}
                  title={
                    i === 0
                      ? `Offered first wherever prompts for “${label}” are listed`
                      : `Number ${i + 1} in the list for “${label}”`
                  }
                >
                  #{i + 1}
                </Badge>
                <h3>{p.name}</h3>
                <Badge tone="accent">{p.mode}</Badge>
                {p.is_builtin ? <Badge>built-in</Badge> : null}
                {!p.is_active ? <Badge tone="warn">inactive</Badge> : null}
                <span className="spacer" />
                <span className="tiny subtle">used {fmtInt(p.use_count)}×</span>
              </div>
              {p.description && <p className="small muted">{p.description}</p>}
              <div
                className="small mono"
                style={{
                  padding: 10,
                  background: 'var(--bg-sunken)',
                  borderRadius: 6,
                  whiteSpace: 'pre-wrap',
                  maxHeight: 120,
                  overflowY: 'auto',
                }}
              >
                {p.instruction}
              </div>
              <div className="row small subtle" style={{ marginTop: 8 }}>
                <span>
                  target <code>{p.target_field}</code>
                </span>
                {p.writes_to && (
                  <span>
                    → writes <code>{p.writes_to}</code>
                  </span>
                )}
                <span>temp {p.temperature}</span>
                {p.model_label ? (
                  <span>
                    model {p.model_label} ({p.provider_name})
                  </span>
                ) : (
                  <span>uses the field&apos;s mapped model</span>
                )}
              </div>
            </div>
            <div className="card-foot">
              {user.caps.update && (
                <>
                  <div className="btn-group" role="group" aria-label={`Priority of ${p.name}`}>
                    <button
                      className="btn btn-sm"
                      onClick={() => onMove(field.value, ids, i, 0)}
                      disabled={busy || i === 0}
                      title="Move to the top of this list"
                    >
                      ⤒ Top
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => onMove(field.value, ids, i, i - 1)}
                      disabled={busy || i === 0}
                      title="Move up"
                      aria-label={`Move ${p.name} up`}
                    >
                      ↑
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => onMove(field.value, ids, i, i + 1)}
                      disabled={busy || i === rows.length - 1}
                      title="Move down"
                      aria-label={`Move ${p.name} down`}
                    >
                      ↓
                    </button>
                  </div>
                  <button className="btn btn-sm" onClick={() => onEdit(p)}>
                    Edit
                  </button>
                  <Switch checked={!!p.is_active} onChange={(on) => onToggle(p, on)} label="Active" />
                </>
              )}
              <span className="spacer" />
              <span className="tiny subtle">updated {fmtDateTime(p.updated_at)}</span>
              {user.caps.delete && !p.is_builtin && (
                <button className="btn btn-sm btn-danger" onClick={() => onDelete(p)}>
                  Delete
                </button>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default function PromptsManager({ user }) {
  const t = useRelabel()
  const query = useApi('prompts', 'prompts')
  const qc = useQueryClient()
  const [editing, setEditing] = useState(null)
  const [creating, setCreating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState('all')
  const toast = useToast()

  // The pickers read /api/lookups, which caches for five minutes. Without this a
  // prompt added or promoted here kept its old place in every dialog until the
  // cache expired — the change looked applied on this page and nowhere else.
  const refresh = async () => {
    await query.refetch()
    await qc.invalidateQueries({ queryKey: ['lookups'] })
  }

  const remove = async () => {
    setBusy(true)
    try {
      await api(`prompts/${confirmDelete.id}`, { method: 'DELETE' })
      toast.ok('Prompt deleted')
      setConfirmDelete(null)
      await refresh()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  const toggleActive = async (p, on) => {
    try {
      await api(`prompts/${p.id}`, { method: 'PUT', body: { is_active: on } })
      await refresh()
    } catch (err) {
      toast.error(err)
    }
  }

  /**
   * Send the whole field's order rather than the one row that moved: ranks are
   * rewritten as 10, 20, 30 … server-side, so there is no arithmetic here about
   * what number an "up" is worth and no way to leave two prompts sharing a place.
   */
  const move = async (targetField, ids, from, to) => {
    if (to < 0 || to >= ids.length || from === to) return
    const next = [...ids]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setBusy(true)
    try {
      await api('prompts/reorder', { method: 'POST', body: { target_field: targetField, ids: next } })
      await refresh()
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <PageHeader
      title="Prompts"
      sub="Reusable AI instructions, filed by what they work on and ordered by priority"
      actions={
        user.caps.create ? (
          <button className="btn btn-sm btn-primary" onClick={() => setCreating(true)}>
            + New Prompt
          </button>
        ) : null
      }
    >
      <QueryState query={query}>
        {(d) => {
          const rowsFor = (value) =>
            d.rows.filter((r) => r.target_field === value).sort(byPriority)
          const groups = promptFieldGroups()
          const shownFields =
            tab === 'all' ? null : PROMPT_FIELDS.filter((f) => f.value === tab)

          const sectionProps = {
            user,
            busy,
            onMove: move,
            onEdit: setEditing,
            onToggle: toggleActive,
            onDelete: setConfirmDelete,
          }

          return (
            <div className="col">
              <Notice icon="info" title="Classification decides where a prompt appears, priority decides how soon">
                A prompt is listed first in the dialog of the field it is filed under, and
                remains selectable in the others under “Your other prompts” — so a
                transcription instruction written here can be used on a photo import, a
                scanned PDF or in the studio without being duplicated. The arrows set the
                order <em>within</em> a field: the prompt at the top is the one an editor is
                offered first for that field, and the one a button with no picker — “Extract
                prayer requests”, the photo importer&apos;s built-in transcriber — falls back
                to when AI Settings → AI Models names no default for the field. Nothing a
                prompt returns is saved until an editor accepts it.
              </Notice>

              {groups.map((g) => (
                <div className="col" key={g.key}>
                  <div className="row">
                    <h2>{g.label}</h2>
                    <span className="small subtle">{g.note}</span>
                  </div>
                  <div className="grid grid-3">
                    {g.fields.map((f) => {
                      const rows = rowsFor(f.value)
                      const top = rows.find((r) => r.is_active)
                      return (
                        <div className="tile" key={f.value}>
                          <div className="tile-label">{t(f.label)}</div>
                          <div className="tile-value">{fmtInt(rows.length)}</div>
                          <div className="tile-note truncate" title={top ? top.name : undefined}>
                            {top ? `first: ${top.name}` : 'none active'}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}

              <Card bodyClass="card-body">
                <Tabs
                  active={tab}
                  onChange={setTab}
                  tabs={[
                    { key: 'all', label: 'All', count: d.rows.length },
                    ...PROMPT_FIELDS.map((f) => ({
                      key: f.value,
                      label: f.label,
                      count: rowsFor(f.value).length,
                    })),
                  ]}
                />

                {(shownFields ?? PROMPT_FIELDS).map((f) => (
                  <FieldSection key={f.value} field={f} rows={rowsFor(f.value)} {...sectionProps} />
                ))}

                {/* A prompt filed under a field this page does not know about — one
                    created before a field key was renamed, say — would otherwise be
                    invisible here while still turning up in the pickers. */}
                {tab === 'all' &&
                  [...new Set(d.rows.map((r) => r.target_field))]
                    .filter((v) => !PROMPT_FIELDS.some((f) => f.value === v))
                    .map((v) => (
                      <FieldSection
                        key={v}
                        field={{
                          value: v,
                          label: promptFieldLabel(v),
                          hint: 'This field key is not one of the standard classifications.',
                        }}
                        rows={rowsFor(v)}
                        {...sectionProps}
                      />
                    ))}
              </Card>

              {creating && (
                <PromptEditor
                  open
                  onClose={() => setCreating(false)}
                  initial={null}
                  onSaved={refresh}
                />
              )}
              {editing && (
                <PromptEditor
                  open
                  onClose={() => setEditing(null)}
                  initial={{
                    id: editing.id,
                    name: editing.name,
                    description: editing.description ?? '',
                    instruction: editing.instruction,
                    target_field: editing.target_field,
                    mode: editing.mode,
                    writes_to: editing.writes_to ?? '',
                    model_id: editing.model_id,
                    temperature: editing.temperature,
                    is_active: !!editing.is_active,
                    is_builtin: !!editing.is_builtin,
                  }}
                  onSaved={refresh}
                />
              )}

              <ConfirmModal
                open={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={remove}
                busy={busy}
                title={`Delete "${confirmDelete?.name}"?`}
                body={
                  <p>
                    The prompt is removed from every picker. Past runs stay in the AI Usage log.
                  </p>
                }
              />
            </div>
          )
        }}
      </QueryState>
    </PageHeader>
  )
}
